import { Button, Col, P4PDayGiftBadge, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import P4PDayFansPanel from './P4PDayFansPanel';
import Section from './Section';
import Toggle from './Toggle';

/**
 * The window is entered and shown in UTC, not in the admin's own timezone.
 *
 * A `datetime-local` input carries no zone, so a browser's default reading of it is local time
 * — which would make the saved instant depend on where the admin happened to be sitting, and
 * make one campaign read differently to two people looking at it. Both helpers below pin the
 * interpretation to UTC, and they must stay symmetrical: if only one of them did, a value
 * would shift by the admin's offset every time the form round-tripped.
 *
 * This is why FanWave's `toDatetimeLocal`/`fromDatetimeLocal` are NOT reused here — those are
 * deliberately local-time, which is the opposite of what is wanted.
 */

/** ISO instant -> the `datetime-local` value, in UTC. `toISOString` is always UTC. */
function toDatetimeUTC(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
}

/** The `datetime-local` value, read as UTC -> the ISO instant the API stores. */
function fromDatetimeUTC(value) {
  if (!value) return null;
  // The trailing Z is what forces the UTC reading; without it this parses as local time.
  // Valid whether or not the browser included seconds.
  const d = new Date(`${value}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * The cutoff is contractual in JST ("fully deactivate at 23:59 JST on October 17"), so each
 * entered instant is echoed in Tokyo time. Read-only — the field itself is the UTC one.
 */
function jstEcho(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Tokyo',
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(d)} JST`;
}

const inputStyle = {
  padding: '10px 12px',
  borderRadius: '8px',
  border: `1px solid ${theme.GREY_20 || '#eee'}`,
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box'
};

const Field = ({ label, hint, children }) => (
  <Col noFlex start wid="100%" marg="0 0 16px">
    <Text b2 bold marg="0 0 6px">
      {label}
    </Text>
    {children}
    {hint && (
      <Text c1 col={theme.GREY_60} marg="6px 0 0" style={{ fontSize: '11px' }}>
        {hint}
      </Text>
    )}
  </Col>
);

const tabularNums = { fontVariantNumeric: 'tabular-nums' };

/**
 * Turns the English gift name stored on the campaign into a locale key: "Key Chain" -> keyChain.
 * Same mapping as `P4PDayFanGiftModal`, so the admin reads the gift the way fans do; a tier the
 * locales have no key for yet falls back to the English the campaign holds.
 */
function giftItemKey(name) {
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)?/g, (_, next) => (next ? next.toUpperCase() : ''));
}

/**
 * One badge figure: what is LEFT is the headline, since that is what booth staff run out of;
 * awarded and the total sit with it.
 */
const BadgeCard = ({ heading, remaining, awarded, total, onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      style={{
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '24px',
        minHeight: '190px',
        padding: '24px',
        boxSizing: 'border-box',
        borderRadius: '14px',
        backgroundColor: theme.PRIMARY_WHITE
      }}
    >
      <Row center between wid="100%" gap="12px" style={{ minHeight: '50px' }}>
        {heading}
        <Text c1 marg="0" col={theme.GREY_60} style={{ ...tabularNums, whiteSpace: 'nowrap' }}>
          {t('admin.p4pDay.total', { count: total.toLocaleString() })}
        </Text>
      </Row>
      <Col noFlex start>
        {/* Trans, not two strings: the number's position in the sentence is the locale's call
            (Japanese puts 残り before it). */}
        <Text
          marg="0"
          col={theme.PRIMARY_BLACK}
          style={{ ...tabularNums, fontSize: '16px', fontWeight: 600 }}
        >
          <Trans
            i18nKey="admin.p4pDay.remaining"
            values={{ count: remaining.toLocaleString() }}
            components={{
              num: (
                <span
                  style={{
                    fontSize: '40px',
                    fontWeight: 700,
                    lineHeight: 1.1
                  }}
                />
              )
            }}
          />
        </Text>
        <Text c1 marg="6px 0 0" col={theme.GREY_60} style={tabularNums}>
          {t('admin.p4pDay.awarded', { count: awarded.toLocaleString() })}
        </Text>
      </Col>
    </div>
  );
};

/**
 * P4P Day campaign settings: the activation window and the runtime kill switch.
 *
 * There are TWO switches and they are not interchangeable, which is why both are shown:
 *
 *   - `P4P_DAY_CAMPAIGN_ENABLED`, an environment variable, decides whether this environment
 *     participates at all. It is not editable here, by design — an admin write must not be a
 *     back door that creates campaign data in an environment that opted out. While it is unset
 *     the server returns no campaign document, refuses every admin write with a 409, and
 *     reports the campaign inactive no matter what window is saved below.
 *   - `isEnabled` below is the runtime kill switch for a campaign that IS participating.
 *
 * So the env var wins. A campaign saved here, in-window and toggled on, still awards nothing
 * the moment that variable is unset — the server's `isActive` check reads it first.
 *
 * Inventory is deliberately not editable here. Rewriting the tiers recomputes stock against
 * what has already been claimed, and that belongs behind a more deliberate screen than a
 * settings tab; `PUT /admin/p4pday/update` still takes it.
 */
const P4PDayTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // null until loaded; `enabled: false` is the environment opt-out, not a load failure.
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ isEnabled: false, startsAt: '', endsAt: '' });
  // The form as last loaded from the server, so Save stays disabled until the admin changes
  // something — there is nothing to save otherwise.
  const [savedForm, setSavedForm] = useState(form);
  // Which card's fans the side panel lists: a colour letter, or null for "All".
  const [panel, setPanel] = useState({ open: false, color: null });

  useEffect(() => {
    fetchCampaign();
  }, []);

  /**
   * `quiet` refreshes the figures without the full-tab spinner — used after a revoke in the side
   * panel, which the spinner would otherwise unmount from under the admin.
   */
  async function fetchCampaign({ quiet = false } = {}) {
    if (!quiet) setLoading(true);

    // Api resolves with a falsy value on failure rather than throwing, and raises its own
    // toast — so there is nothing to catch here, only a value to check.
    const res = await Api.get('/admin/p4pday/get');
    setLoading(false);
    if (!res?.data) return;

    setData(res.data);

    const campaign = res.data.campaign;
    // A quiet refresh is only after the counts; reloading the form would throw away whatever
    // the admin has typed into the window fields and not saved yet.
    if (campaign && !quiet) {
      const loaded = {
        isEnabled: !!campaign.isEnabled,
        startsAt: toDatetimeUTC(campaign.startsAt),
        endsAt: toDatetimeUTC(campaign.endsAt)
      };
      setForm(loaded);
      setSavedForm(loaded);
    }
  }

  async function handleSave() {
    const startsAt = fromDatetimeUTC(form.startsAt);
    const endsAt = fromDatetimeUTC(form.endsAt);

    if (!startsAt || !endsAt) {
      alert(t('admin.p4pDay.datesRequired'));
      return;
    }

    // Checked here as well as read as a date: an end before the start leaves a campaign that
    // can never be active, and the server would accept it without complaint.
    if (new Date(endsAt) <= new Date(startsAt)) {
      alert(t('admin.p4pDay.endBeforeStart'));
      return;
    }

    setSaving(true);
    const res = await Api.put('/admin/p4pday/update', {
      isEnabled: form.isEnabled,
      startsAt,
      endsAt
    });
    setSaving(false);
    if (!res) return;

    // Re-read rather than trusting the local form: `active` is the server's verdict on the
    // window it actually stored, and it is the thing an admin came here to check.
    fetchCampaign();
  }

  if (loading) {
    return (
      <Col wid="100%" ht="400px" centerAll noFlex>
        <Spinner2 />
      </Col>
    );
  }

  if (!data) {
    return (
      <Col start noFlex wid="100%" pad="24px">
        <Text med col={theme.GREY_60}>
          {t('admin.p4pDay.loadFailed')}
        </Text>
      </Col>
    );
  }

  // The environment opted out. No form: every write would be refused with a 409, and an
  // editable window here would imply a campaign that cannot run.
  if (!data.enabled) {
    return (
      <Col start noFlex wid="100%" pad="24px">
        <Section
          title={t('admin.p4pDay.tab')}
          description={t('admin.p4pDay.disabledSectionDescription')}
        >
          <Col
            noFlex
            start
            wid="100%"
            pad="16px"
            style={{
              borderRadius: '10px',
              border: `1px solid ${theme.GREY_20 || '#eee'}`,
              backgroundColor: '#fff'
            }}
          >
            <Text b2 bold marg="0 0 6px">
              {t('admin.p4pDay.disabledTitle')}
            </Text>
            <Text c1 col={theme.GREY_60} marg="0" style={{ lineHeight: 1.6 }}>
              {t('admin.p4pDay.disabledDescription')}
            </Text>
          </Col>
        </Section>
      </Col>
    );
  }

  const startsIso = fromDatetimeUTC(form.startsAt);
  const endsIso = fromDatetimeUTC(form.endsAt);
  const isDirty =
    form.isEnabled !== savedForm.isEnabled ||
    form.startsAt !== savedForm.startsAt ||
    form.endsAt !== savedForm.endsAt;

  const inventory = data.campaign?.inventory || [];
  const grandTotal = inventory.reduce((sum, item) => sum + item.total, 0);
  const grandRemaining = inventory.reduce((sum, item) => sum + item.remaining, 0);
  const grandDistributed = grandTotal - grandRemaining;

  return (
    <Col start noFlex wid="100%" pad="24px" gap="32px">
      <Section title={t('admin.p4pDay.campaignTitle')}>
        <Col
          noFlex
          start
          wid="100%"
          pad="20px"
          style={{
            borderRadius: '10px',
            border: `1px solid ${theme.GREY_20 || '#eee'}`,
            backgroundColor: '#fff'
          }}
        >
          <Row center between wid="100%" marg="0 0 20px">
            <Col style={{ flex: 1 }}>
              <Text b2 bold marg="0">
                {t('admin.p4pDay.enabledLabel')}
              </Text>
            </Col>
            <Toggle
              on={form.isEnabled}
              onToggle={() => setForm((prev) => ({ ...prev, isEnabled: !prev.isEnabled }))}
            />
          </Row>

          <Field
            label={t('admin.p4pDay.startsAt')}
            hint={t('admin.p4pDay.inTokyo', { time: jstEcho(startsIso) })}
          >
            <input
              type="datetime-local"
              style={inputStyle}
              value={form.startsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
            />
          </Field>

          <Field
            label={t('admin.p4pDay.endsAt')}
            hint={t('admin.p4pDay.inTokyo', { time: jstEcho(endsIso) })}
          >
            <input
              type="datetime-local"
              style={inputStyle}
              value={form.endsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
            />
          </Field>

          <Row center gap="8px" marg="4px 0 0">
            <Text b2 bold marg="0">
              {t('admin.p4pDay.currently')}
            </Text>
            <Text b2 bold marg="0" col={data.active ? theme.GREEN : theme.GREY_60}>
              {data.active ? t('admin.p4pDay.active') : t('admin.p4pDay.notActive')}
            </Text>
          </Row>
        </Col>
      </Section>

      {/* Directly under the campaign card and above Badges: it saves only the campaign
          fields, and Badges below is read-only. */}
      <Row marg="-8px 0 0" centerAll>
        <Button
          text={saving ? t('admin.p4pDay.saving') : t('admin.p4pDay.save')}
          onClick={handleSave}
          bg={theme.PRIMARY}
          loading={saving}
          disabled={!isDirty || saving}
          wid="200px"
        />
      </Row>

      <Section title={t('admin.p4pDay.badgesTitle')}>
        <div
          style={{
            marginTop: '16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            width: '100%'
          }}
        >
          <BadgeCard
            heading={
              // A size up from the tiers' gift names: what marks this card as the sum.
              <Text bold marg="0" col={theme.PRIMARY_BLACK} style={{ fontSize: '22px' }}>
                {t('admin.p4pDay.allBadges')}
              </Text>
            }
            remaining={grandRemaining}
            awarded={grandDistributed}
            total={grandTotal}
            onClick={() => setPanel({ open: true, color: null })}
          />
          {inventory.map((item) => (
            <BadgeCard
              key={item.color}
              heading={
                <Row center gap="10px" title={item.colorName}>
                  <P4PDayGiftBadge giftColor={item.color} />
                  <Text c1 bold marg="0">
                    {t(`p4pDay.giftItems.${giftItemKey(item.giftItem)}`, {
                      defaultValue: item.giftItem
                    })}
                  </Text>
                </Row>
              }
              remaining={item.remaining}
              awarded={item.total - item.remaining}
              total={item.total}
              onClick={() => setPanel({ open: true, color: item.color })}
            />
          ))}
        </div>
      </Section>

      <Row marg="0 0 48px" />

      <P4PDayFansPanel
        visible={panel.open}
        color={panel.color}
        // The gift, as the badge card names it — staff think in "the key chains", not "RED".
        title={(() => {
          const tier = inventory.find((item) => item.color === panel.color);
          return tier
            ? t(`p4pDay.giftItems.${giftItemKey(tier.giftItem)}`, { defaultValue: tier.giftItem })
            : null;
        })()}
        onClose={() => setPanel((prev) => ({ ...prev, open: false }))}
        onChanged={() => fetchCampaign({ quiet: true })}
      />
    </Col>
  );
};

export default P4PDayTab;
